import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getCurrentBaseTitle } from "components/DocumentTitle";
import { clearClockSettingsCache, getCurrentUserId } from "utils/clockSettings";
import { pushRegistrar } from "pushNotifications";

const useInactivityTimer = (timeoutMinutes = 30) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const timeoutDuration = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
  const updateTitle = useCallback((remainingTime) => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    const baseTitle = getCurrentBaseTitle();

    // Only show timer in title if remaining time is less than 10 minutes
    if (remainingTime <= 10 * 60 * 1000) {
      // 10 minutes in milliseconds
      document.title = `${baseTitle} | ${minutes}:${seconds.toString().padStart(2, "0")}`;
    } else {
      document.title = baseTitle;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // Reset title when user is active
    document.title = getCurrentBaseTitle();

    timeoutRef.current = setTimeout(() => {
      const userId = getCurrentUserId();
      pushRegistrar.release();
      // Clear React Query cache
      queryClient.clear();
      clearClockSettingsCache(userId);
      // Clear any stored tokens or user data
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userDetail");
      localStorage.removeItem("loginTime");
      // Redirect to login page
      navigate("/login");
    }, timeoutDuration);

    // Start countdown timer
    let remainingTime = timeoutDuration;
    countdownRef.current = setInterval(() => {
      remainingTime -= 1000;
      if (remainingTime <= 0) {
        clearInterval(countdownRef.current);
      } else {
        updateTitle(remainingTime);
      }
    }, 1000);
  }, [timeoutDuration, navigate, updateTitle, queryClient]);

  useEffect(() => {
    // Events to track
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Initial timer setup
    resetTimer();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      document.title = getCurrentBaseTitle();
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);

  return { resetTimer };
};

export default useInactivityTimer;
